import { useContext, useEffect, useState } from "react";
import "cesium/Build/Cesium/Widgets/widgets.css";
import {
  Cartesian3,
  createWorldTerrainAsync,
  GeoJsonDataSource,
  Ion,
  IonImageryProvider,
  KmlDataSource,
  Viewer,
  viewerDragDropMixin,
} from "cesium";
import {
  AreaMeasure,
  DistanceMeasure,
  DistanceSurfaceMeasure,
} from "@cesium-extends/measure";
import { PiPolygonLight } from "react-icons/pi";
import { GiPathDistance } from "react-icons/gi";
import { MdOutlinePolyline } from "react-icons/md";
import { Loading } from "../../common/components/loading/loading";
import { MdOutlineMyLocation } from "react-icons/md";
import dxfParser from "dxf-parser";
import proj4 from "proj4";
import shp from "shpjs";
import ReactSwitch from "react-switch";
import Context from "../../common/components/utils/context";
import { Themes } from "../../common/enums/themes";
import { Units } from "@turf/helpers";
import { BlobServiceClient } from "@azure/storage-blob";
import { ShapeFilesV1Service } from "../../common/services/api/api";
import { IShapeFile } from "./models";
import { environment } from "../../common/env";
import { Utils } from "../../common/helpers/utils";

export const CesiumViewer = () => {
  const context = useContext(Context);
  const [shapeFiles, setShapeFiles] = useState<IShapeFile[]>(
    context.shapeFiles
  );
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState("FT");
  const [enabled3DBuildings, setEnabled3DBuildings] = useState(true);
  const [label3DBuildings, setLabel3DBuildings] = useState("3D Buildings");

  function getCurrentPosition() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(success, error);
    } else {
      console.log("Geolocation not supported!");
    }
  }

  async function success(position: any) {
    var groundDistanceUnits: Units = "meters";
    const cesiumAccessToken = environment.Cesium_Default_Access_Token;

    setLoading(true);
    const lg = Number(position.coords.longitude);
    const la = Number(position.coords.latitude);

    const altitude = await Utils.getElevation(la, lg);

    Ion.defaultAccessToken = cesiumAccessToken;
    setLoading(false);
    var viewer = new Viewer("cesiumContainer", {
      terrainProvider: await createWorldTerrainAsync(),
      animation: false,
      timeline: false,
      homeButton: true,
      sceneModePicker: false,
      baseLayerPicker: false,
      navigationHelpButton: false,
      shouldAnimate: true,
      requestRenderMode: true,
    });
    viewer.extend(viewerDragDropMixin);
    try {
      const imageryLayer = viewer.imageryLayers.addImageryProvider(
        await IonImageryProvider.fromAssetId(2)
      );
      await viewer.zoomTo(imageryLayer);
      await flyToMyLocation();
    } catch (error) {
      console.error(`Error creating tileset: ${error}`);
    }

    const area = new AreaMeasure(viewer, {
      onEnd: (entity) => {
        document.getElementById("areaUnitContainer")!.hidden = false;
        const positions = entity.polyline?.positions?.getValue();
        const areaMeasure = area.getArea(positions).toFixed(2);
        document.getElementById("areaResultValue")!.innerHTML = areaMeasure;
        AreaUnitsConverter(Number(areaMeasure));
        document.getElementById("saveCesiumMeasurement")!.hidden = false;
        const cancelArea = document.getElementById(
          "cancelCesiumMeasurement"
        ) as HTMLButtonElement;
        cancelArea.addEventListener("click", () => {
          area.end();
          AreaUnitsConverter(0);
          ResetMeasurement();
          document.getElementById("areaUnitContainer")!.hidden = true;
          cancelArea.replaceWith(cancelArea.cloneNode(true));
        });
        const uploadArea = document.getElementById(
          "uploadCesiumMeasurement"
        ) as HTMLButtonElement;
        uploadArea.addEventListener("click", () => {
          context.setAlert({
            message: "Coming Soon!",
            theme: Themes.Success,
          });
          area.end();
          AreaUnitsConverter(0);
          ResetMeasurement();
          document.getElementById("areaUnitContainer")!.hidden = true;
          uploadArea.replaceWith(uploadArea.cloneNode(true));
        });
      },
    });

    const nonGroundDistance = new DistanceMeasure(viewer, {
      onEnd: (entity) => {
        document.getElementById("distanceUnitContainer")!.hidden = false;
        const nonGroundDistanceMeasure = GetDistanceMeasure(
          entity,
          "nonGroundDistance"
        );
        const totalDistance = Math.floor(nonGroundDistanceMeasure * 100) / 100;
        document.getElementById("distanceResultValue")!.innerHTML =
          totalDistance.toString();
        DistanceUnitsConverter(totalDistance);
        document.getElementById("saveCesiumMeasurement")!.hidden = false;
        const cancelNonGroundDistance = document.getElementById(
          "cancelCesiumMeasurement"
        ) as HTMLButtonElement;
        cancelNonGroundDistance.addEventListener("click", () => {
          nonGroundDistance.end();
          DistanceUnitsConverter(0);
          ResetMeasurement();
          document.getElementById("distanceUnitContainer")!.hidden = true;
          cancelNonGroundDistance.replaceWith(
            cancelNonGroundDistance.cloneNode(true)
          );
        });
        const uploadNonGroundDistance = document.getElementById(
          "uploadCesiumMeasurement"
        ) as HTMLButtonElement;
        uploadNonGroundDistance.addEventListener("click", () => {
          context.setAlert({
            message: "Coming Soon!",
            theme: Themes.Success,
          });
          nonGroundDistance.end();
          DistanceUnitsConverter(0);
          ResetMeasurement();
          document.getElementById("distanceUnitContainer")!.hidden = true;
          uploadNonGroundDistance.replaceWith(
            uploadNonGroundDistance.cloneNode(true)
          );
        });
      },
    });

    const groundDistance = new DistanceSurfaceMeasure(viewer, {
      units: groundDistanceUnits,
      locale: {
        start: "Start",
        total: "Total",
        formatLength: (length) => {
          return length + groundDistanceUnits;
        },
      },
      drawerOptions: {
        tips: {
          init: "Click to draw",
          start:
            "Left click to add points, right click to remove points, double click to end drawing",
        },
      },
      onEnd: (entity) => {
        document.getElementById("distanceUnitContainer")!.hidden = false;
        const groundDistanceMeasure = GetDistanceMeasure(
          entity,
          "groundDistance"
        );
        const totalDistance = Math.floor(groundDistanceMeasure * 100) / 100;
        document.getElementById("distanceResultValue")!.innerHTML =
          totalDistance.toString();
        DistanceUnitsConverter(totalDistance);
        document.getElementById("saveCesiumMeasurement")!.hidden = false;
        const cancelGroundDistance = document.getElementById(
          "cancelCesiumMeasurement"
        ) as HTMLButtonElement;
        cancelGroundDistance.addEventListener("click", () => {
          groundDistance.end();
          DistanceUnitsConverter(0);
          ResetMeasurement();
          document.getElementById("distanceUnitContainer")!.hidden = true;
          cancelGroundDistance.replaceWith(
            cancelGroundDistance.cloneNode(true)
          );
        });
        const uploadGroundDistance = document.getElementById(
          "uploadCesiumMeasurement"
        ) as HTMLButtonElement;
        uploadGroundDistance.addEventListener("click", () => {
          context.setAlert({
            message: "Coming Soon!",
            theme: Themes.Success,
          });
          groundDistance.end();
          DistanceUnitsConverter(0);
          ResetMeasurement();
          document.getElementById("distanceUnitContainer")!.hidden = true;
          uploadGroundDistance.replaceWith(
            uploadGroundDistance.cloneNode(true)
          );
        });
      },
    });

    var measureGroundArea = document.getElementById("groundArea");
    measureGroundArea?.addEventListener("click", MeasureGroundArea);
    measureGroundArea?.removeEventListener("click", () => {});
    var measureNonGroundDistance = document.getElementById("nonGroundDistance");
    measureNonGroundDistance?.addEventListener(
      "click",
      MeasureNonGroundDistance
    );
    measureNonGroundDistance?.removeEventListener("click", () => {});
    var measureGroundDistance = document.getElementById("groundDistance");
    measureGroundDistance?.addEventListener("click", MeasureGroundDistance);
    measureGroundDistance?.removeEventListener("click", () => {});

    function ResetMeasurement(): void {
      (document.getElementById(
        "saveMeasurementInputName"
      ) as HTMLInputElement)!.value = "";
      (document.getElementById(
        "saveObjectInputName"
      ) as HTMLInputElement)!.value = "";
      var distanceResultTitle = (document.getElementById(
        "resultTitle"
      ) as HTMLDivElement)!.textContent;
      var distanceResultValue = document.getElementById(
        "distanceResultValue"
      ) as HTMLDivElement;
      if (
        (distanceResultTitle === "Ground Distance" ||
          distanceResultTitle === "Non-Ground Distance") &&
        distanceResultValue!.textContent !== ""
      ) {
        distanceResultValue!.innerHTML = "";
      }
      var areaResultTitle = (document.getElementById(
        "areaResultTitle"
      ) as HTMLDivElement)!.textContent;
      var areaResultValue = document.getElementById(
        "areaResultValue"
      ) as HTMLDivElement;
      if (
        areaResultTitle === "Ground Area" &&
        areaResultValue!.textContent !== ""
      ) {
        areaResultValue!.innerHTML = "";
      }
      document.getElementById("saveCesiumMeasurement")!.hidden = true;
    }

    function GetDistanceMeasure(entity: any, measurement: string): number {
      const positions = entity.polyline?.positions?.getValue();
      let groundDistanceMeasure = 0;
      for (let i = 0; i < positions.length - 1; i++) {
        const position = positions[i];
        const pos2 = positions[i + 1];
        const distance =
          measurement === "groundDistance"
            ? groundDistance.getDistance(position, pos2)
            : nonGroundDistance.getDistance(position, pos2);
        groundDistanceMeasure += distance;
      }
      return groundDistanceMeasure;
    }

    function DistanceUnitsConverter(distance: number): void {
      document.getElementById("_m")?.addEventListener("click", () => {
        document.getElementById("distanceResultValue")!.innerHTML =
          distance.toString();
        groundDistanceUnits = "meters";
      });
      document.getElementById("_ft")?.addEventListener("click", () => {
        var MeterToFootConversionRate = 3.2808399;
        var conversion = distance * MeterToFootConversionRate;
        var distanceInFeet = conversion.toFixed(2);
        document.getElementById("distanceResultValue")!.innerHTML =
          distanceInFeet.toString();
        groundDistanceUnits = "feet";
      });
      document.getElementById("_miles")?.addEventListener("click", () => {
        var MeterToMileConversionRate = 0.000621371192;
        var conversion = distance * MeterToMileConversionRate;
        var distanceInMiles = conversion.toFixed(3);
        document.getElementById("distanceResultValue")!.innerHTML =
          distanceInMiles.toString();
        groundDistanceUnits = "miles";
      });
      document.getElementById("_km")?.addEventListener("click", () => {
        var distanceInKilometers = (distance / 100 / 10).toFixed(3);
        document.getElementById("distanceResultValue")!.innerHTML =
          distanceInKilometers.toString();
        groundDistanceUnits = "kilometers";
      });
    }

    function AreaUnitsConverter(area: number): void {
      document.getElementById("_m2")?.addEventListener("click", () => {
        document.getElementById("areaResultValue")!.innerHTML = area.toString();
      });
      document.getElementById("_ft2")?.addEventListener("click", () => {
        var conversionRate = 10.7639104;
        var conversion = area * conversionRate;
        var areaInFeet2 = conversion.toFixed(2);
        document.getElementById("areaResultValue")!.innerHTML =
          areaInFeet2.toString();
      });
      document.getElementById("_yd2")?.addEventListener("click", () => {
        var conversionRate = 1.19599005;
        var conversion = area * conversionRate;
        var areaInYd2 = conversion.toFixed(3);
        document.getElementById("areaResultValue")!.innerHTML =
          areaInYd2.toString();
      });
      document.getElementById("_mi2")?.addEventListener("click", () => {
        var conversionRate = 2590000;
        var conversion = area / conversionRate;
        var areaInMi2 = conversion.toFixed(10);
        document.getElementById("areaResultValue")!.innerHTML =
          areaInMi2.toString();
      });
      document.getElementById("_in2")?.addEventListener("click", () => {
        var conversionRate = 1550.0031;
        var conversion = area * conversionRate;
        var areaInIn2 = conversion.toFixed(2);
        document.getElementById("areaResultValue")!.innerHTML =
          areaInIn2.toString();
      });
      document.getElementById("_km2")?.addEventListener("click", () => {
        var conversionRate = 1000000;
        var conversion = area / conversionRate;
        var longDecimal = conversion.toString().substring(0, 4);
        let formatted;
        if (
          conversion < 1 &&
          longDecimal === "0.00" &&
          conversion.toString().length > 11
        ) {
          formatted = new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 8,
            maximumFractionDigits: 8,
          }).format(conversion);
        } else {
          formatted = conversion;
        }
        var areaInKm2 = formatted;
        document.getElementById("areaResultValue")!.innerHTML =
          areaInKm2.toString();
      });
      document.getElementById("_ac")?.addEventListener("click", () => {
        var conversionRate = 4046.85642;
        var conversion = area / conversionRate;
        var areaInAcres = conversion.toFixed(6);
        document.getElementById("areaResultValue")!.innerHTML =
          areaInAcres.toString();
      });
      document.getElementById("_ha")?.addEventListener("click", () => {
        var conversionRate = 10000;
        var conversion = area / conversionRate;
        var areaInHa = conversion.toFixed(6);
        document.getElementById("areaResultValue")!.innerHTML =
          areaInHa.toString();
      });
    }

    function MeasureGroundArea(): void {
      ResetMeasurement();
      document.getElementById("areaUnitContainer")!.hidden = true;
      SetMeasurementView("groundArea");
      area.end();
      area.start();
    }

    function MeasureNonGroundDistance(): void {
      ResetMeasurement();
      document.getElementById("distanceUnitContainer")!.hidden = true;
      SetMeasurementView("nonGroundDistance");
      nonGroundDistance.end();
      nonGroundDistance.start();
    }

    function MeasureGroundDistance(): void {
      ResetMeasurement();
      document.getElementById("distanceUnitContainer")!.hidden = true;
      SetMeasurementView("groundDistance");
      groundDistance.end();
      groundDistance.start();
    }

    function SetMeasurementView(id: string) {
      switch (id) {
        case "groundDistance":
          document.getElementById("distanceMeasurementResults")!.hidden = false;
          document.getElementById("areaMeasurementResults")!.hidden = true;
          document.getElementById("resultTitle")!.innerText = "Ground Distance";
          document.getElementById("distanceResultValue")!.innerHTML = "";
          document.getElementById("groundDistance")!.style.backgroundColor =
            "#ffffff";
          document.getElementById("nonGroundDistance")!.style.backgroundColor =
            "#1a1a3e";
          document.getElementById("groundArea")!.style.backgroundColor =
            "#1a1a3e";
          (document.getElementById("_m") as HTMLInputElement).checked = true;
          break;
        case "nonGroundDistance":
          document.getElementById("distanceMeasurementResults")!.hidden = false;
          document.getElementById("areaMeasurementResults")!.hidden = true;
          document.getElementById("resultTitle")!.innerText =
            "Non-Ground Distance";
          document.getElementById("distanceResultValue")!.innerHTML = "";
          document.getElementById("groundDistance")!.style.backgroundColor =
            "#1a1a3e";
          document.getElementById("nonGroundDistance")!.style.backgroundColor =
            "#ffffff";
          document.getElementById("groundArea")!.style.backgroundColor =
            "#1a1a3e";
          (document.getElementById("_m") as HTMLInputElement).checked = true;
          break;
        case "groundArea":
          document.getElementById("distanceMeasurementResults")!.hidden = true;
          document.getElementById("areaMeasurementResults")!.hidden = false;
          document.getElementById("areaResultTitle")!.innerText = "Ground Area";
          document.getElementById("areaResultValue")!.innerHTML = "";
          document.getElementById("groundDistance")!.style.backgroundColor =
            "#1a1a3e";
          document.getElementById("nonGroundDistance")!.style.backgroundColor =
            "#1a1a3e";
          document.getElementById("groundArea")!.style.backgroundColor =
            "#ffffff";
          (document.getElementById("_m2") as HTMLInputElement).checked = true;
          break;
        default:
          break;
      }
    }

    var myLocation = document.getElementById("myLocation") as HTMLDivElement;
    myLocation!.addEventListener("click", () => {
      flyToMyLocation();
    });
    myLocation?.removeEventListener("click", () => {});

    async function flyToMyLocation() {
      viewer.camera.flyTo({
        destination: Cartesian3.fromDegrees(lg, la, altitude + 300),
      });
    }

    var fileImported: any = null;
    var importFileValue = document.getElementById(
      "importFileValue"
    ) as HTMLInputElement;

    var view2DfilesSelect = document.getElementById(
      "view2DfilesSelect"
    ) as HTMLSelectElement;

    view2DfilesSelect.addEventListener("change", async () => {
      var view2DfileId =
        view2DfilesSelect.options[view2DfilesSelect.selectedIndex].value;
      if (view2DfileId) {
        const newSelection = shapeFiles.find(
          (shapeFile: IShapeFile) => shapeFile.id === view2DfileId
        );
        if (newSelection) {
          var view2DObjectResult = await view2DObject(newSelection.url);
          if (view2DObjectResult.errorMessage) {
            context.setAlert({
              message: `Failed to display object!
               ${view2DObjectResult.errorMessage}`,
              theme: Themes.Danger,
            });
          }
        }
      } else {
        flyToMyLocation();
      }
    });

    var resetImportBtn = document.getElementById(
      "cancelImport"
    ) as HTMLButtonElement;

    var importBtn = document.getElementById(
      "importCesiumObject"
    ) as HTMLButtonElement;

    importBtn.addEventListener("click", async () => {
      importBtn.setAttribute("disabled", "disabled");
      fileImported = null;
      fileImported = await import2DObject();
      if (fileImported.errorMessage) {
        context.setAlert({
          message: `Failed to import file!
           ${fileImported.errorMessage}`,
          theme: Themes.Danger,
        });
        importFileValue.removeAttribute("disabled");
        importBtn.removeAttribute("disabled");
      } else {
        importFileValue.setAttribute("disabled", "disabled");
        document.getElementById("saveCesiumObject")!.hidden = false;
      }
    });

    resetImportBtn.addEventListener("click", async () => {
      fileImported = null;
      const ft = document.getElementById("fileTypeSelect") as HTMLSelectElement;
      if (ft.selectedIndex !== 0) {
        ft.selectedIndex = 0;
        importFileValue.value = "";
        importFileValue.removeAttribute("disabled");
        importBtn.removeAttribute("disabled");
        (
          document.getElementById("saveObjectInputName") as HTMLInputElement
        ).value = "";
        document.getElementById("saveCesiumObject")!.hidden = true;
        await flyToMyLocation();
      }
    });

    async function url2blob(url: string) {
      try {
        const data = await fetch(url);
        const blob = await data.blob();
        return blob;
      } catch (err) {
        console.error(err);
        return null;
      }
    }

    async function import2DObject() {
      var errorMessage;
      var inputFile: File | undefined;
      var files = (
        document.getElementById("importFileValue") as HTMLInputElement
      ).files;
      if (!files || files.length === 0) {
        errorMessage = "No file selected!";
      } else {
        inputFile = files[0];
        const fileExtension = inputFile.name
          .split(".")
          .pop()
          ?.toLocaleLowerCase();
        var selectedFT = document.getElementById(
          "fileTypeSelect"
        ) as HTMLSelectElement;
        var textFT = selectedFT.options[selectedFT.selectedIndex].value;
        switch (textFT) {
          case "FT":
            errorMessage = "Please select a file type!";
            break;
          case "SHP":
            if (fileExtension !== "zip") {
              errorMessage = "Please select a .zip file!";
            } else {
              try {
                const data = await inputFile.arrayBuffer();
                const geojson = await shp(data);
                const shpDataSource = await GeoJsonDataSource.load(geojson, {
                  clampToGround: true,
                });
                await viewer.dataSources.add(shpDataSource);
                var zoomedToSHP = viewer.zoomTo(shpDataSource);
                if (!zoomedToSHP) {
                  errorMessage = "Unable to load SHP file!";
                }
              } catch (error) {
                errorMessage = "Unable to load SHP file!";
              }
            }
            break;
          case "DXF":
            if (fileExtension !== "dxf") {
              errorMessage = "Please select a .dxf file!";
            } else {
              const file = files[0];
              const reader = new FileReader();
              reader.onload = async (e: any) => {
                try {
                  const parser = new dxfParser();
                  const dxfData = parser.parseSync(e.target.result);
                  const geoJSONData = convertDXFToGeoJSON(dxfData);
                  const dxfDataSource = await GeoJsonDataSource.load(
                    geoJSONData,
                    {
                      clampToGround: true,
                    }
                  );
                  await viewer.dataSources.add(dxfDataSource);

                  var zoomedToDXF = viewer.zoomTo(dxfDataSource);
                  if (!zoomedToDXF) {
                    errorMessage = "Unable to load DXF file!";
                  }
                } catch (error) {
                  errorMessage = "Unable to load DXF file!";
                }
              };
              reader.readAsText(file);
            }
            break;
          case "KML":
            if (fileExtension !== "kml" && fileExtension !== "kmz") {
              errorMessage = "Please select a .kml or .kmz file!";
            } else {
              try {
                const kmlDataSource = await KmlDataSource.load(inputFile, {
                  camera: viewer.scene.camera,
                  canvas: viewer.scene.canvas,
                  clampToGround: true,
                });
                await viewer.dataSources.add(kmlDataSource);
                var zoomedToKML = await viewer.zoomTo(kmlDataSource);
                if (!zoomedToKML) {
                  errorMessage = "Unable to load KML file!";
                }
              } catch (error) {
                errorMessage = "Unable to load KML file!";
              }
            }
            break;
          default:
            break;
        }
      }
      var newFileObj = {
        inputFile: inputFile,
        errorMessage: errorMessage,
      };
      return newFileObj;
    }

    async function view2DObject(selectedFileUrl: string) {
      var errorMessage;
      var inputFile: File | undefined;
      var blob = await url2blob(selectedFileUrl);
      if (blob) {
        var selectedFileName = selectedFileUrl.split("/").pop() || "";
        var files = [new File([blob], selectedFileName)];
        inputFile = files[0];
        const fileExtension = inputFile.name
          .split(".")
          .pop()
          ?.toLocaleLowerCase();
        if (fileExtension === "zip") {
          try {
            const data = await inputFile.arrayBuffer();
            const geojson = await shp(data);
            const shpDataSource = await GeoJsonDataSource.load(geojson, {
              clampToGround: true,
            });
            await viewer.dataSources.add(shpDataSource);
            var zoomedToSHP = viewer.zoomTo(shpDataSource);
            if (!zoomedToSHP) {
              errorMessage = "Unable to load SHP file!";
            }
          } catch (error) {
            errorMessage = "Unable to load SHP file!";
          }
        } else if (fileExtension === "dxf") {
          const file = files[0];
          const reader = new FileReader();
          reader.onload = async (e: any) => {
            try {
              const parser = new dxfParser();
              const dxfData = parser.parseSync(e.target.result);
              const geoJSONData = convertDXFToGeoJSON(dxfData);
              const dxfDataSource = await GeoJsonDataSource.load(geoJSONData, {
                clampToGround: true,
              });
              await viewer.dataSources.add(dxfDataSource);
              var zoomedToDXF = viewer.zoomTo(dxfDataSource);
              if (!zoomedToDXF) {
                errorMessage = "Unable to load DXF file!";
              }
            } catch (error) {
              errorMessage = "Unable to load DXF file!";
            }
          };
          reader.readAsText(file);
        } else if (fileExtension === "kml" || fileExtension === "kmz") {
          try {
            const kmlDataSource = await KmlDataSource.load(inputFile, {
              camera: viewer.scene.camera,
              canvas: viewer.scene.canvas,
              clampToGround: true,
            });
            await viewer.dataSources.add(kmlDataSource);
            var zoomedToKML = await viewer.zoomTo(kmlDataSource);
            if (!zoomedToKML) {
              errorMessage = "Unable to load KML file!";
            }
          } catch (error) {
            errorMessage = "Unable to load KML file!";
          }
        } else {
          errorMessage = "No valid .zip, .dxf, .kml or .kmz file!";
        }
      }
      var newFileObj = {
        inputFile: inputFile,
        errorMessage: errorMessage,
      };
      return newFileObj;
    }

    const toggle3DBuildings = () => {
      // Change the imagery provider to OpenStreetMap
      //if (viewer) {
      //  viewer.imageryLayers.removeAll(); // Remove the default imagery layer
      //  viewer.imageryLayers.addImageryProvider(new OpenStreetMapImageryProvider());
      //  viewer.scene.mode = is3D ? SceneMode.SCENE2D : SceneMode.SCENE3D;
      //}
    };
  }
  function error() {
    context.setAlert({
      message:
        "Unable to retrieve your location. Please try again or contact your administrator.",
      theme: Themes.Danger,
    });
    console.log("Unable to retrieve your location!!!");
  }

  const handleFileTypeChanged = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedFile(event.target.value);
  };

  const convertDXFToGeoJSON = (dxfData: any) => {
    const sourceProjection =
      "+proj=utm +zone=44 +datum=WGS84 +units=m +no_defs";
    const targetProjection = "+proj=longlat +datum=WGS84 +no_defs";
    const geoJSONFeatures = dxfData.entities
      .map((entity: any) => {
        if (
          entity.type === "LWPOLYLINE" ||
          entity.type === "POLYLINE" ||
          entity.type === "LINE"
        ) {
          const coordinates = entity.vertices.map((vertex: any) => {
            const [x, y] = proj4(sourceProjection, targetProjection, [
              vertex.x,
              vertex.y,
            ]);
            return [x, y];
          });
          return {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: coordinates,
            },
            properties: {},
          };
        } else {
          return null;
        }
      })
      .filter(Boolean);
    return {
      type: "FeatureCollection",
      features: geoJSONFeatures,
    };
  };

  useEffect(() => {
    const initializeAndloadData = async () => {
      getCurrentPosition();
    };
    initializeAndloadData();
  }, []);

  function handleToggle3DBuildings(e: boolean): void {
    setEnabled3DBuildings(!enabled3DBuildings);
    if (e) {
      setLabel3DBuildings("3D Buildings");
    } else {
      setLabel3DBuildings("2D Buildings");
    }
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className="md:flex">
          <div className="w-screen h-2/5 md:h-screen relative">
            <div id="cesiumContainer" className="relative h-full w-full"></div>
          </div>
          <div className="toggle-3dbuildings-container" hidden={loading}>
            <label
              className="toggle-3dbuildings-label"
              htmlFor="google-3DBuildings"
            >
              {label3DBuildings}
            </label>
            <ReactSwitch
              checked={enabled3DBuildings}
              onChange={(e) => handleToggle3DBuildings(e)}
              height={30}
              width={60}
              onColor="#2DE1FC"
              offColor="#A9A9A9"
              checkedIcon={<span role="img" aria-label="sound-on"></span>}
              uncheckedIcon={<span role="img" aria-label="sound-off"></span>}
              id="google-3DBuildings"
              handleDiameter={30}
              disabled
            />
          </div>
          <div className="view-2dfiles-container" hidden={loading}>
            <div className="upload-header">View 2D Files</div>
            <select
              id="view2DfilesSelect"
              name="view2DfilesSelect"
              className="file-type-selected"
            >
              <option className="file-type-option" value="">
                Select...
              </option>
              {shapeFiles.map((item) => {
                return (
                  <option
                    className="file-type-option"
                    key={item.id}
                    value={item.id}
                  >
                    {item.displayName}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="measurement-header" hidden={loading}>
            Measurements
          </div>
          <div className="measurements" hidden={loading}>
            <div id="groundArea">
              <PiPolygonLight size={30} color="#b0e0e6" title="Area" />
            </div>
            <div id="nonGroundDistance">
              <MdOutlinePolyline
                size={30}
                color="#b0e0e6"
                title="Non-Ground Distance"
              />
            </div>
            <div id="groundDistance">
              <GiPathDistance
                size={30}
                color="#b0e0e6"
                title="Ground Distance"
              />
            </div>
          </div>
          <div
            className="measurement-results"
            id="distanceMeasurementResults"
            hidden
          >
            <div className="result-title" id="resultTitle"></div>
            <div
              id="distanceUnitContainer"
              className="distanceUnitContainer"
              hidden
            >
              <input
                type="radio"
                name="distance-unit"
                id="_m"
                value="meter"
                className="left-space"
              />
              <label htmlFor="_m">m</label>
              <input
                type="radio"
                name="distance-unit"
                id="_ft"
                value="ft"
                className="left-space"
              />
              <label htmlFor="_ft">ft</label>
              <input
                type="radio"
                name="distance-unit"
                id="_miles"
                value="miles"
                className="left-space"
              />
              <label htmlFor="_miles">miles</label>
              <input
                type="radio"
                name="distance-unit"
                id="_km"
                value="km"
                className="left-space"
              />
              <label htmlFor="_km">km</label>
            </div>
            <div
              id="distanceResultValue"
              className="distance-result-value"
            ></div>
          </div>
          <div
            className="measurement-results"
            id="areaMeasurementResults"
            hidden
          >
            <div className="result-title" id="areaResultTitle"></div>
            <div id="areaUnitContainer" hidden>
              <div className="distanceUnitContainer">
                <input
                  type="radio"
                  name="area-unit"
                  id="_m2"
                  value="m2"
                  className="left-space"
                />
                <label className="area-unit-label" htmlFor="_m2">
                  m&sup2;
                </label>
                <input
                  type="radio"
                  name="area-unit"
                  id="_ft2"
                  value="ft2"
                  className="left-space"
                />
                <label className="area-unit-label" htmlFor="_ft2">
                  ft&sup2;
                </label>
                <input
                  type="radio"
                  name="area-unit"
                  id="_yd2"
                  value="yd2"
                  className="left-space"
                />
                <label className="area-unit-label" htmlFor="_yd2">
                  yd&sup2;
                </label>
                <input
                  type="radio"
                  name="area-unit"
                  id="_km2"
                  value="km2"
                  className="left-space"
                />
                <label className="area-unit-label" htmlFor="_km2">
                  km&sup2;
                </label>
              </div>
              <div className="distanceUnitContainer">
                <input
                  type="radio"
                  name="area-unit"
                  id="_in2"
                  value="in2"
                  className="left-space"
                />
                <label className="area-unit-label" htmlFor="_in2">
                  in&sup2;
                </label>
                <input
                  type="radio"
                  name="area-unit"
                  id="_mi2"
                  value="mi2"
                  className="left-space"
                />
                <label className="area-unit-label" htmlFor="_mi2">
                  mi&sup2;
                </label>
                <input
                  type="radio"
                  name="area-unit"
                  id="_ac"
                  value="ac"
                  className="left-space"
                />
                <label className="area-unit-label" htmlFor="_ac">
                  ac
                </label>
                <input
                  type="radio"
                  name="area-unit"
                  id="_ha"
                  value="ha"
                  className="left-space"
                />
                <label className="area-unit-label" htmlFor="_ha">
                  ha
                </label>
              </div>
            </div>
            <div id="areaResultValue" className="distance-result-value"></div>
          </div>
          <div
            className="save-measurement-container"
            id="saveCesiumMeasurement"
            hidden
          >
            <div className="upload-body">
              <input
                type="text"
                id="saveMeasurementInputName"
                className="cesium-input-control"
                placeholder="Name*"
                required
              />
            </div>
            <div className="cesium-btn-footer">
              <button
                type="button"
                className="cesium-cancel-btn"
                id="cancelCesiumMeasurement"
              >
                Cancel
              </button>
              <button
                type="button"
                className="cesium-submit-btn"
                id="uploadCesiumMeasurement"
              >
                Save
              </button>
            </div>
          </div>
          <div className="upload-container" hidden={loading}>
            <div className="upload-header">Import 2D Files</div>
            <div className="upload-body">
              <select
                id="fileTypeSelect"
                name="fileTypeSelect"
                className={
                  selectedFile === "FT"
                    ? "file-type-selected"
                    : "cesium-input-control"
                }
                value={selectedFile}
                onChange={handleFileTypeChanged}
              >
                <option className="file-type-option" value="FT">
                  File Type*
                </option>
                <option value="SHP">SHP (.zip)</option>
                <option value="DXF">DXF (.dxf)</option>
                <option value="KML">KML (.kml, .kmz)</option>
              </select>
              <input
                type="file"
                id="importFileValue"
                name="importFileValue"
                accept=".zip, .dxf, .kml, .kmz"
              />
            </div>
            <div className="cesium-btn-footer">
              <button
                type="button"
                className="cesium-cancel-btn"
                id="cancelImport"
              >
                Reset
              </button>
              <button
                type="button"
                className="cesium-submit-btn"
                id="importCesiumObject"
              >
                Import
              </button>
            </div>
          </div>
          <div className="save-object-container" id="saveCesiumObject" hidden>
            <div className="upload-body">
              <input
                type="text"
                id="saveObjectInputName"
                name="saveObjectInputName"
                className="cesium-input-control"
                placeholder="Description*"
                required
              />
            </div>
            <div className="cesium-btn-footer">
              <button
                type="button"
                className="cesium-submit-btn"
                id="uploadCesiumObject"
                onClick={async () => {
                  const importButton = document.getElementById(
                    "importCesiumObject"
                  ) as HTMLButtonElement;
                  var errorMsg: string = "";
                  const accountName = "foundatblobsuat";
                  const containerName = "earthtwin";
                  const sasToken = environment.SAS_Token as string;
                  var files = (
                    document.getElementById(
                      "importFileValue"
                    ) as HTMLInputElement
                  ).files;
                  var file = files![0];
                  var inputDescription = (document.getElementById(
                    "saveObjectInputName"
                  ) as HTMLInputElement)!.value.trim();
                  if (inputDescription === "") {
                    errorMsg = "Please enter a name for the object!";
                    context.setAlert({
                      message: errorMsg,
                      theme: Themes.Danger,
                    });
                  } else {
                    const now = new Date();
                    const utcMilllisecondsSinceEpoch =
                      now.getTime() + now.getTimezoneOffset() * 60 * 1000;
                    const utcSecondsSinceEpoch = Math.round(
                      utcMilllisecondsSinceEpoch / 1000
                    ).toString();
                    const blobName = utcSecondsSinceEpoch + "-" + file.name;
                    try {
                      importButton.setAttribute("disabled", "disabled");
                      const blobServiceClient = new BlobServiceClient(
                        `https://${accountName}.blob.core.windows.net?${sasToken}`
                      );
                      const containerClient =
                        blobServiceClient.getContainerClient(containerName);
                      await containerClient.createIfNotExists();
                      const blockBlobClient =
                        containerClient.getBlockBlobClient(blobName);
                      const uploadBlobResponse =
                        await blockBlobClient.uploadData(file);
                      if (uploadBlobResponse._response.status === 201) {
                        const uploaded = `https://${accountName}.blob.core.windows.net/${containerName}/${blobName}`;
                        const shapeFilesService = new ShapeFilesV1Service();
                        var newShapeFile = await shapeFilesService.create({
                          displayName: inputDescription,
                          url: uploaded,
                          accountId: context.account.accountId,
                        });
                        if (newShapeFile.status === 201) {
                          const newFile = newShapeFile.data;
                          var newData = [newFile, ...shapeFiles];
                          setShapeFiles(newData);
                          (document.getElementById(
                            "fileTypeSelect"
                          ) as HTMLSelectElement)!.selectedIndex = 0;
                          const importFileValue = document.getElementById(
                            "importFileValue"
                          ) as HTMLInputElement;
                          importFileValue.value = "";
                          importFileValue.removeAttribute("disabled");
                          importButton.removeAttribute("disabled");
                          (document.getElementById(
                            "saveObjectInputName"
                          ) as HTMLInputElement)!.value = "";
                          document.getElementById("saveCesiumObject")!.hidden =
                            true;
                          context.setAlert({
                            message: `File uploaded successfully!`,
                            theme: Themes.Success,
                          });
                        } else {
                          importButton.removeAttribute("disabled");
                          errorMsg = "Unable to save file!";
                          context.setAlert({
                            message: errorMsg,
                            theme: Themes.Danger,
                          });
                        }
                      } else {
                        importButton.removeAttribute("disabled");
                        errorMsg = "Upload to blob storage failed!";
                        context.setAlert({
                          message: errorMsg,
                          theme: Themes.Danger,
                        });
                      }
                    } catch (error) {
                      importButton.removeAttribute("disabled");
                      errorMsg = "Something went wrong: Unable to upload file!";
                      context.setAlert({
                        message: errorMsg,
                        theme: Themes.Danger,
                      });
                    }
                  }
                }}
              >
                Save
              </button>
            </div>
          </div>

          <div
            className="my-location-container"
            id="myLocation"
            hidden={loading}
          >
            <MdOutlineMyLocation size={20} />
          </div>
        </div>
      )}
    </>
  );
};
